import { createReducer } from '@reduxjs/toolkit';
import {
  login,
  logout,
  getUserContact,
  getUserGeneral,
  setUserLanguage,
  getProInfo,
  getAllBannerLogos,
  getChangePassword,
  profileUtils,
  getSoftware,
  getUserPrimaryContact,
  getUserSecondaryContact,
  getUserAccountingContact,
  getPharmacyInfo,
  getUserCoordinates,
  updateUserGeneral,
  setPreLoginLangWeb,
  forgotPassword,
  setRequestReset,
  verifyOtp,
  getFaq,
  getUsername,
  resetName,
  getTermsAndConditions,
  setLocation,
  setAddress,
  resetValidOtp,
  resetUpdateOperations,
  resetSignedUp,
  resetApproval,
  setUserProps,
  doesEmailExist,
  setSignedUp,
  smartLogin,
  setStartTour,
  getUserPreferredTheme,
  setUserPreferredTheme,
  getUserConfigurations,
  sendOtp,
  authVerifyOtp,
  setEmailChangeReset,
  resetLoggedIn,
  setUserType,
  resetProfileImage,
  getClientAdvertisements,
  getBilling,
  getRequestOrBookedPharmacies,
  getRequestOrBookedPharmacists,
  getRequestOrBookedPharmaciesPagination,
  getRequestOrBookedPharmacistsPagination,
  getPharmacyBilling,
  resetBilling,
  showCriticalMissedNotification,
  initiateSignup,
  professionalUploadSignupDiploma,
  professionalDeleteSignupDiploma,
  validatePromoToken,
  resetPromoTokenValidity,
  createProfile,
  resetPromoUsed,
  resetSignupProfilePortingStatus,
  createProfessionalProfile,
  getPharmaPlanInvitationPreview,
  resetPharmaPlanInvitationPreview,
} from '../actions/userActions';
import httpClient from '../controllers/httpClient';
import {
  responseType,
  TypeOfUser,
  TimeFormat,
  SocialLoginType,
  Language,
  ColorScheme,
  LoginType,
} from '../helpers/Constants';
import {
  IContact,
  IFaq,
  IGeneral,
  ILogos,
  IProfessionalInfo,
  ISoftware,
  IUtils,
} from '../models/PharmacistModels';
import AvatarFallback from '@pharmaplan/common/assets/icons/avatarPlaceholder.png';
import {
  getAdminPicture,
  getAdminSettings,
  getPharmacistPicture,
  getPharmacyAdminPicture,
  getPharmacyAdminProfile,
  impersonatePharmacy,
  professionalDeleteDiploma,
} from '../actions';
import { convertBase64ToImage, encodeImageUrl } from '../helpers/utils';
import {
  ICoordinates,
  IPharmacyBilling,
  IPharmacyInfo,
  IProfessionalBilling,
  IUserPropsModel,
} from '../models';
import serverDateHandler from '../helpers/serverDateHandler';
import {
  IClientAdvertisement,
  IListOfRequestedBookedUsers,
  IListOfRequestedBookedUsersResponse,
  ISignupRequest,
  IUserConfigurations,
} from '../models/Models';
import LogoutHandler from '../helpers/LogoutHandler';
import { genericPagination } from './adminReducer';
import {
  IPharmaPlanInvitationPreview,
  ISignupProfilePortingStatus,
} from '../models/CrossPlatformModels';

const getSignupProfilePortingStatus = (
  response: Partial<ISignupProfilePortingStatus> | null,
): ISignupProfilePortingStatus | null => {
  if (!response?.createPharmaPlanProfile) {
    return null;
  }

  return {
    createPharmaPlanProfile: true,
    ppProfileCreated: response.ppProfileCreated === true,
  };
};

export interface IUserReducerState {
  pharmaPlanInvitationPreview: IPharmaPlanInvitationPreview | null;
  signupProfilePortingStatus: ISignupProfilePortingStatus | null;
  isPharmacyAdminLogin: boolean;
  isAvatar: boolean;
  email: string;
  userId: string;
  userProps: IUserPropsModel;
  contact: IContact;
  userName: string;
  name: string;
  isFirstTimeLogin: boolean | null;
  isValidPromoToken: {
    isValid: boolean;
    registrationPopupUrl: string;
  } | null;
  enforceProfileUpdate: boolean;
  requestedReset: boolean;
  rememberMeHash: string;
  isValidOtp: boolean;
  isLoggedIn: boolean;
  isSignedUp: boolean;
  isApproved: boolean;
  general: IGeneral;
  profileImage: string | any;
  language: Language;
  preLoginWebLang: string;
  userType: TypeOfUser;
  proInfo: IProfessionalInfo;
  software: Array<ISoftware>;
  logos: Array<ILogos>;
  changePassword: boolean;
  userLocation: {
    latitude: number | null;
    longitude: number | null;
  };
  utils: IUtils;
  coordinates: ICoordinates;
  faq: Array<IFaq>;
  primaryContact: any;
  secondaryContact: any;
  accountingContact: any;
  pharmacyInfo: IPharmacyInfo;
  preferredLanguage: Language;
  timeFormat: TimeFormat;
  termsAndConditionsHTMLTemplate: string;
  address: string;
  emailExists: boolean | null;
  startTour: boolean;
  userTheme: ColorScheme;
  userConfigurations: IUserConfigurations;
  didRequestChangeEmail: boolean;
  enforceLogout: boolean;
  enableChat: boolean;
  advertisement: Array<IClientAdvertisement>;
  billing: IProfessionalBilling;
  bookedRequestedPharmacists: IListOfRequestedBookedUsersResponse;
  bookedRequestedPharmacies: IListOfRequestedBookedUsersResponse;
  pharmacyBilling: IPharmacyBilling;
  displaySelectPackagePopup: boolean;
  displayMissedNotificationMessage: boolean;
  signupRequest: ISignupRequest;
  promoUsed: boolean | null;
  diplomaUrl: { certificateUrl: string; success: boolean };
}

export const userInitialState: IUserReducerState = {
  pharmaPlanInvitationPreview: null,
  signupProfilePortingStatus: null,
  diplomaUrl: {
    certificateUrl: '',
    success: false,
  },
  signupRequest: {
    signupRequestId: null,
  },
  displayMissedNotificationMessage: false,
  pharmacyBilling: {
    currentPlan: null,
    renewalDate: '',
    planFees: 0,
    isSubscribed: false,
    creditCardLast4: null,
    brand: null,
    cardType: null,
    basicFees: 0,
    unlimitedFees: 0,
    activeUntil: null,
    paymentStatus: null,
    isEligibleForRefund: false,
    neverSubscribed: false,
  },
  bookedRequestedPharmacies: {
    ...genericPagination,
    data: [],
  },
  bookedRequestedPharmacists: {
    ...genericPagination,
    data: [],
  },
  billing: {
    accountNumber: '',
    branchNumber: '',
    institutionNumber: '',
  },
  advertisement: [],
  isPharmacyAdminLogin: false,
  enableChat: true,
  didRequestChangeEmail: false,
  emailExists: null,
  isAvatar: false,
  userId: '',
  userConfigurations: { minHourWorkshift: 4, numberOfDaysToRatePharmacist: 14 },
  userProps: {
    email: '',
    password: '',
    appid: '',
    token: '',
    loginType: SocialLoginType.facebook,
    device: {
      deviceId: '',
      model: '',
      type: '',
      appVersion: '',
      build: '',
      osType: '',
      osVersion: '',
      deviceToken: null,
    },
  },
  profileImage: AvatarFallback,
  email: '',
  enforceProfileUpdate: false,
  userName: '',
  isLoggedIn: false,
  isValidPromoToken: null,
  language: Language.en,
  preLoginWebLang: Language.en,
  isSignedUp: false,
  requestedReset: false,
  isValidOtp: false,
  faq: [],
  userType: TypeOfUser.pharmacy,
  rememberMeHash: '',
  contact: {
    address: '',
    avilabilityReminder: false,
    city: '',
    homePhone: '',
    mobile: '',
    postalcode: '',
    preferredReplacementDuration: '',
    preferredWorkingDays: [],
    province: '',
    //Setting this as the default value because this is the client's location
    latitude: 45.5019,
    longitude: -73.5674,
    prefferedReplacementDurations: [],
  },
  general: {
    firstName: '',
    signupType: LoginType.pharmaplan,
    lastName: '',
    preferdLanguage: '',
    timeFormat: TimeFormat.twentyFourHours,
    preferredWorkingDays: '',
    prefferedReplacementDurations: [],
    notifyIfSelected: false,
  },
  termsAndConditionsHTMLTemplate: '',
  software: [],
  secondaryContact: {
    secondaryContactName: '',
    secondaryContactPhone: '',
    secondaryContactPhoneExt: '',
    secondaryContactMobile: '',
    secondaryContactEmail: '',
  },
  primaryContact: {
    primaryContactName: '',
    primaryContactPhone: '',
    primaryContactPhoneExt: '',
    primaryContactMobile: '',
    primaryContactPhoneTech: '',
    primaryContactPhoneExtTech: '',
    primaryContactEmail: '',
  },
  accountingContact: {
    accountingContactName: '',
    accountingContactPhone: '',
    accountingContactPhoneExt: '',
    accountingContactMobile: '',
    accountingContactEmail: '',
  },
  coordinates: {
    phone: '',
    fax: '',
    emergencyPhone: '',
    address: '',
    city: '',
    province: '',
    postalCode: '',
    latitude: 45.5019,
    longitude: -73.5674,
    provinces: [],
  },
  userLocation: {
    latitude: 45.5019,
    longitude: -73.5674,
  },
  pharmacyInfo: {
    name: '',
    languageId: '',
    number: 0,
    softwareId: '',
    pharmacyChainId: '',
    selectedContactPreferences: [],
    weekVolume: '',
    weekendVolume: '',
    weekDayTechs: '',
    weekNightTechs: '',
    weekendDayTechs: '',
    weekendNightTechs: '',
    weekDayPharmacists: '',
    weekNightPharmacists: '',
    weekendDayPharmacists: '',
    weekendNightPharmacists: '',
    logo: '',
    email: '',
    hourlyRate: 0,
    timeFormat: TimeFormat.twentyFourHours,
    softwares: [],
    contactPreferences: [],
  },
  proInfo: {
    professionalCapacities: [],
    englishLevel: '',
    frenchLevel: '',
    graduationYear: '',
    incorporated: false,
    licenseNumber: '',
    otherLanguages: null,
    pharmacistCapacityDesc: '',
    pharmacistCapacityId: '',
    yearsOfExperience: '',
    preferdLanguage: '',
    pharmacistCapacities: [],
    userType: TypeOfUser.pharmacist,
    reviewDetails: {
      arrivalTotalReviews: 0,
      arrivals: {},
      cancellationPercent: 0,
      cancellations: 0,
      confirmedBookings: 0,
      questionValues: [],
      totalBookings: 0,
    },
  },
  logos: [],
  changePassword: false,
  isApproved: true,
  preferredLanguage: Language.en,
  timeFormat: TimeFormat.twentyFourHours,
  utils: { softwares: [], banners: [], contactPreferences: [] },
  address: '',
  startTour: false,
  userTheme: ColorScheme.auto,
  enforceLogout: false,
  name: '',
  displaySelectPackagePopup: false,
  promoUsed: null,
  isFirstTimeLogin: null,
};

const userReducer = createReducer(userInitialState, (builder) => {
  builder
    .addCase(login.fulfilled, (state, action) => {
      const {
        approved,
        userType,
        enforceProfileUpdate,
        language,
        serverDate,
        userId,
        name,
        email,
        enableChat,
        displaySelectPackagePopup,
        isFirstTimeLogin,
        displayMissedNotificationMessage,
      } = action.payload ?? {};
      if (approved) {
        state.enforceProfileUpdate = enforceProfileUpdate;
        state.isPharmacyAdminLogin = userType === TypeOfUser.pharmacyAdmin;
        state.userId = userId;
        serverDateHandler.setDate(serverDate);
        state.userType = userType;
        state.language = language?.code;
        state.isLoggedIn = !!userId;
        state.name = name;
        state.email = email;
        state.enableChat = enableChat;
        state.isFirstTimeLogin = isFirstTimeLogin;
        state.displaySelectPackagePopup = displaySelectPackagePopup;
        state.displayMissedNotificationMessage =
          displayMissedNotificationMessage;
        LogoutHandler.userIsLoggedIn();
      } else {
        state.isApproved = approved;
      }
    })
    .addCase(smartLogin.fulfilled, (state, action) => {
      const {
        userType,
        id,
        language,
        approved,
        serverDate,
        userId,
        name,
        email,
      } = action.payload ?? {};
      if (approved) {
        const { code } = language;
        state.userType = userType;
        state.userId = userId;
        serverDateHandler.setDate(serverDate);
        state.isSignedUp = !id;
        state.language = code;
        state.isLoggedIn = !state.isSignedUp;
        state.isApproved = approved;
        state.name = name;
        state.email = email;
      } else {
        state.isApproved = approved;
      }
    })
    //reset all state on logout
    .addCase(logout.fulfilled, (state) => {
      LogoutHandler.userIsLoggedOut();
      return { ...userInitialState, preLoginWebLang: state.preLoginWebLang };
    })
    .addCase(getUserContact.fulfilled, (state, action) => {
      state.contact = action.payload;
    })
    .addCase(setUserProps, (state, action) => {
      state.userProps = action.payload;
      state.userType = action.payload.userType as TypeOfUser;
    })
    .addCase(doesEmailExist.fulfilled, (state, action) => {
      state.emailExists = action.payload;
    })
    .addCase(getUserPrimaryContact.fulfilled, (state, action) => {
      state.primaryContact = action.payload;
    })
    .addCase(getUserSecondaryContact.fulfilled, (state, action) => {
      state.secondaryContact = action.payload;
    })
    .addCase(getUserAccountingContact.fulfilled, (state, action) => {
      state.accountingContact = action.payload;
    })
    .addCase(resetApproval, (state) => {
      state.isApproved = true;
      state.emailExists = null;
    })
    .addCase(getUserGeneral.fulfilled, (state, action) => {
      state.general = action.payload;
      state.preferredLanguage = action.payload.preferdLanguage;
      state.email = action.payload.email;
      state.timeFormat = action.payload.timeFormat;
    })
    .addCase(getUserCoordinates.fulfilled, (state, action) => {
      state.coordinates = action.payload;
    })
    .addCase(setUserLanguage, (state, action) => {
      httpClient.defaults.headers.common['locale'] =
        action.payload ?? Language.en;
      state.language = action.payload as Language;
    })
    .addCase(setPreLoginLangWeb, (state, action) => {
      httpClient.defaults.headers.common['locale'] = action.payload;
      state.preLoginWebLang = action.payload;
    })
    .addCase(getProInfo.fulfilled, (state, action) => {
      state.proInfo = action.payload;
    })
    .addCase(getPharmacyInfo.fulfilled, (state, action) => {
      state.pharmacyInfo = action.payload;
      state.preferredLanguage = action.payload.languageId;
      state.profileImage = action.payload.logo;
      state.email = action.payload.email;
      state.timeFormat = action.payload.timeFormat;
    })
    .addCase(getAllBannerLogos.fulfilled, (state, action) => {
      state.logos = action.payload;
    })
    .addCase(getChangePassword.fulfilled, (state, action) => {
      state.changePassword = action.payload.success;
    })
    .addCase(profileUtils.fulfilled, (state, action) => {
      state.utils = action.payload;
    })
    .addCase(getPharmacistPicture.fulfilled, (state, action) => {
      const pictureUrl = encodeImageUrl(action.payload.url);
      state.isAvatar = !!pictureUrl;
      state.profileImage = pictureUrl || AvatarFallback;
    })
    .addCase(forgotPassword.fulfilled, (state, action) => {
      state.requestedReset = action.payload?.key === responseType.success;
    })
    .addCase(setRequestReset, (state, action) => {
      state.requestedReset = action.payload;
    })
    .addCase(setEmailChangeReset, (state, action) => {
      state.didRequestChangeEmail = action.payload;
    })
    .addCase(verifyOtp.fulfilled, (state, action) => {
      state.isValidOtp = action.payload?.isValidOTP;
    })
    .addCase(authVerifyOtp.fulfilled, (state, action) => {
      state.isValidOtp = action.payload?.isValidOTP;
      state.enforceLogout = action.payload?.enforceLogout;
    })
    .addCase(resetValidOtp, (state) => {
      state.isValidOtp = false;
    })
    .addCase(getFaq.fulfilled, (state, action) => {
      state.faq = action.payload;
    })
    .addCase(getUsername.fulfilled, (state, action) => {
      state.userName = action.payload;
    })
    .addCase(resetName, (state) => {
      state.userName = '';
    })
    .addCase(getSoftware.fulfilled, (state, action) => {
      state.software = action.payload;
    })
    .addCase(getTermsAndConditions.fulfilled, (state, action) => {
      state.termsAndConditionsHTMLTemplate = action.payload.content;
    })
    .addCase(setLocation, (state, action) => {
      state.userLocation = action.payload;
    })
    .addCase(setAddress, (state, action) => {
      state.address = action.payload;
    })
    .addCase(resetUpdateOperations, (state) => {
      state.enforceProfileUpdate = userInitialState.enforceProfileUpdate;
    })
    .addCase(resetSignedUp, (state) => {
      state.isSignedUp = userInitialState.isSignedUp;
    })
    .addCase(setSignedUp, (state, action) => {
      state.isSignedUp = action.payload;
    })
    .addCase(setStartTour, (state, action) => {
      state.startTour = action.payload;
    })
    .addCase(getUserPreferredTheme.fulfilled, (state, action) => {
      state.userTheme = action.payload;
    })
    .addCase(getUserConfigurations.fulfilled, (state, action) => {
      state.userConfigurations = action.payload;
    })
    .addCase(getAdminSettings.fulfilled, (state, action) => {
      state.userConfigurations.minHourWorkshift =
        action.payload?.minHourWorkshift;
    })
    .addCase(sendOtp.fulfilled, (state, action) => {
      state.didRequestChangeEmail =
        action.payload?.key === responseType.success;
    })
    .addCase(resetLoggedIn, (state, action) => {
      state.isLoggedIn = false;
    })
    .addCase(setUserType, (state, action) => {
      state.userType = action.payload;
    })
    .addCase(initiateSignup.fulfilled, (state, action) => {
      state.signupRequest = action.payload;
    })
    .addCase(getPharmaPlanInvitationPreview.pending, (state) => {
      state.pharmaPlanInvitationPreview = null;
    })
    .addCase(getPharmaPlanInvitationPreview.fulfilled, (state, action) => {
      state.pharmaPlanInvitationPreview = action.payload;
    })
    .addCase(resetPharmaPlanInvitationPreview, (state) => {
      state.pharmaPlanInvitationPreview = null;
    })
    .addCase(resetProfileImage, (state) => {
      state.profileImage = AvatarFallback;
    })
    .addCase(setUserPreferredTheme.fulfilled, (state, action) => {
      state.userTheme = action.meta.arg;
    })
    .addCase(getPharmacyAdminProfile.fulfilled, (state, action) => {
      state.name = action.payload?.pharmacyCompany?.user?.userName;
    })
    .addCase(getClientAdvertisements.fulfilled, (state, action) => {
      state.advertisement = action.payload;
    })
    .addCase(getAdminPicture.fulfilled, (state, action) => {
      state.isAvatar = !!action.payload.image;
      state.profileImage = action.payload.image
        ? convertBase64ToImage(action.payload.image, action.payload.contentType)
        : AvatarFallback;
    })
    .addCase(impersonatePharmacy.fulfilled, (state, action) => {
      state.userId = action.payload?.userId;
    })
    .addCase(getBilling.fulfilled, (state, action) => {
      state.billing = action.payload;
    })
    .addCase(getPharmacyBilling.fulfilled, (state, action) => {
      state.pharmacyBilling = action.payload;
    })
    .addCase(resetBilling, (state) => {
      state.pharmacyBilling = userInitialState.pharmacyBilling;
    })
    .addCase(getRequestOrBookedPharmacies.fulfilled, (state, action) => {
      state.bookedRequestedPharmacies = action.payload;
    })
    .addCase(
      getRequestOrBookedPharmaciesPagination.fulfilled,
      (state, action) => {
        state.bookedRequestedPharmacies.data.push(...action.payload?.data);
      },
    )
    .addCase(getRequestOrBookedPharmacists.fulfilled, (state, action) => {
      state.bookedRequestedPharmacists = action.payload;
    })
    .addCase(
      getRequestOrBookedPharmacistsPagination.fulfilled,
      (state, action) => {
        state.bookedRequestedPharmacists.data = action.payload;
      },
    )
    .addCase(showCriticalMissedNotification, (state, action) => {
      state.displayMissedNotificationMessage = action.payload;
    })
    .addCase(professionalUploadSignupDiploma.fulfilled, (state, action) => {
      state.diplomaUrl = action.payload;
    })
    .addCase(createProfile.fulfilled, (state, action) => {
      state.promoUsed = action.payload?.promoUsed;
      state.signupProfilePortingStatus = getSignupProfilePortingStatus(
        action.payload ?? null,
      );
    })
    .addCase(createProfessionalProfile.fulfilled, (state, action) => {
      state.promoUsed = action.payload?.promoUsed;
      state.signupProfilePortingStatus = getSignupProfilePortingStatus(
        action.payload ?? null,
      );
    })
    .addCase(resetPromoUsed, (state, action) => {
      state.promoUsed = null;
    })
    .addCase(resetSignupProfilePortingStatus, (state) => {
      state.signupProfilePortingStatus = null;
    })
    .addCase(validatePromoToken.fulfilled, (state, action) => {
      state.isValidPromoToken = action.payload;
    })
    .addCase(resetPromoTokenValidity, (state, action) => {
      state.isValidPromoToken = null;
    })
    .addCase(professionalDeleteSignupDiploma.fulfilled, (state, action) => {
      state.diplomaUrl = {
        certificateUrl: '',
        success: false,
      };
    })
    .addCase(getPharmacyAdminPicture.fulfilled, (state, action) => {
      state.isAvatar = !!action.payload.image;
      state.profileImage = action.payload.image
        ? convertBase64ToImage(action.payload.image, action.payload.contentType)
        : AvatarFallback;
    });
});

export default userReducer;
